import { useId } from 'react';
import { Form } from '../../payload-types';
import { cn } from '../../utilities/classname';
import FormFieldView from './form-field-view';
import AltchaWidget from './altcha-widget';
import { FormPrivacyPolicyLink } from './form-privacy-policy-link';

type FormField = NonNullable<Form['fields']>[number];

/** Derive a stable ID and matching default value for a single form field. */
export function resolveFieldEntry(
  field: FormField,
  index: number,
  defaultValues?: Record<string, string>,
): { id: string; defaultValue: string | undefined } {
  return {
    id: String(field.id ?? index),
    defaultValue: field.type ? defaultValues?.[field.type] : undefined,
  };
}

/**
 * A form's fields. Every field's `id` (and its label's `for`) carries a prefix
 * unique to this instance: the same form can be on a page twice (a page's form
 * block and a header form-button, or navbar-one's banner CTA and its copy
 * pinned in the phone menu), and with the bare Payload row ids both copies'
 * labels would point at the first copy's inputs, leaving the second's
 * unnamed. Only ever rendered inside client components.
 */
const FormView: React.FC<{
  form: Form;
  isPending?: boolean;
  action: (payload: FormData) => void;
  defaultValues?: Record<string, string>;
  fixedFields?: Record<string, string>;
  submitText?: string;
  submitClassName?: string;
  actionClassName?: string;
  /** Extra classes for the privacy policy line under the submit row (e.g. centring it). */
  privacyPolicyClassName?: string;
  layout?: 'contact-panel';
}> = ({
  form,
  isPending,
  action,
  defaultValues,
  fixedFields,
  submitText = 'Submit',
  submitClassName,
  actionClassName,
  privacyPolicyClassName,
  layout,
}) => {
  const isContactPanel = layout === 'contact-panel';
  const idPrefix = useId();
  const fields = form.fields?.map((field, i) => {
    const { id, defaultValue } = resolveFieldEntry(field, i, defaultValues);

    if (field.type && fixedFields?.[field.type] != null) {
      return <input key={id} type="hidden" name={field.type} value={fixedFields[field.type]} />;
    }

    return (
      <FormFieldView key={id} id={`${idPrefix}-${id}`} field={field} defaultValue={defaultValue} layout={layout} />
    );
  });

  return (
    <form action={action} className={isContactPanel ? 'flex flex-1 flex-col gap-3.5' : undefined}>
      <input type="hidden" name="form" value={form.id} />

      {/* Honeypot — hidden from real users, filled by bots */}
      <div
        aria-hidden="true"
        style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, pointerEvents: 'none' }}
      >
        <input type="text" name="website_url" tabIndex={-1} autoComplete="off" />
      </div>

      {isContactPanel ? <div className="grid grid-cols-1 gap-3.5 lg:grid-cols-2">{fields}</div> : fields}

      <AltchaWidget />

      <div className={actionClassName ?? (isContactPanel ? 'mt-auto pt-0.5' : 'modal-action')}>
        <button
          type="submit"
          className={cn('btn btn-primary', isContactPanel && 'btn-lg w-full', submitClassName)}
          disabled={isPending}
        >
          {isPending ? (
            <>
              <span className="loading loading-spinner loading-xs" />
              Submitting
            </>
          ) : submitText}
        </button>
      </div>

      {/* The contact panel spaces its rows with `gap`; everywhere else the line needs its own margin. */}
      <FormPrivacyPolicyLink
        policy={form.privacyPolicy}
        className={cn(!isContactPanel && 'mt-3', privacyPolicyClassName)}
      />
    </form>
  );
};

export default FormView;
